import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, MapPin, User, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { canViewEventByAudience, getMemberGroupIds } from '@/lib/eventAudienceVisibility';

export default function Agenda() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openedFromHomeEventId, setOpenedFromHomeEventId] = useState(null);
  const parseEventDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).slice(0, 10);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return new Date(dateStr);
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const { data: allEvents = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => base44.entities.Event.filter({ status: 'Ativo' }, 'date', 100)
  });

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
    retry: false
  });

  const { data: currentMember } = useQuery({
    queryKey: ['currentMember', currentUser?.email],
    queryFn: () => base44.entities.Member.filter({ email: currentUser.email }),
    enabled: !!currentUser?.email
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.filter({ is_active: true })
  });

  // Filter events based on user's audiences
  const member = currentMember?.[0] || null;
  const memberGroupIds = getMemberGroupIds(member, groups);
  const events = allEvents.filter((event) =>
    canViewEventByAudience(event, { member, memberGroupIds })
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get first day of week for the month (0 = Sunday)
  const startDay = monthStart.getDay();
  
  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => event.date === dateStr);
  };

  // Get events for selected date
  const parseTimeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return Number.MAX_SAFE_INTEGER;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return Number.MAX_SAFE_INTEGER;
    return hours * 60 + minutes;
  };

  const sortEventsByDateAndTime = (eventList) => {
    return [...eventList].sort((a, b) => {
      const dateA = parseEventDate(a.date);
      const dateB = parseEventDate(b.date);

      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }

      return parseTimeToMinutes(a.start_time) - parseTimeToMinutes(b.start_time);
    });
  };

  const selectedDateEvents = selectedDate ? sortEventsByDateAndTime(getEventsForDate(selectedDate)) : [];
  const currentMonthEvents = sortEventsByDateAndTime(events.filter((event) => {
    const eventDate = parseEventDate(event.date);
    return (
      eventDate.getMonth() === currentMonth.getMonth() &&
      eventDate.getFullYear() === currentMonth.getFullYear()
    );
  }));
  const visibleEvents = selectedDate ? selectedDateEvents : currentMonthEvents;
  const selectedEventDayEvents = selectedEvent
    ? sortEventsByDateAndTime(getEventsForDate(parseEventDate(selectedEvent.date)))
    : [];
  const selectedEventIndex = selectedEventDayEvents.findIndex((event) => event.id === selectedEvent?.id);
  const showAdjacentEvent = (offset) => {
    if (selectedEventDayEvents.length < 2) return;
    const nextIndex = (selectedEventIndex + offset + selectedEventDayEvents.length) % selectedEventDayEvents.length;
    setSelectedEvent(selectedEventDayEvents[nextIndex]);
  };

  const eventTypeColors = {
    'Culto': 'bg-indigo-100 text-indigo-700',
    'Reunião': 'bg-blue-100 text-blue-700',
    'Vigília': 'bg-purple-100 text-purple-700',
    'Escola Bíblica': 'bg-amber-100 text-amber-700',
    'Conferência': 'bg-emerald-100 text-emerald-700',
    'Encontro': 'bg-rose-100 text-rose-700',
    'Outro': 'bg-gray-100 text-gray-700',
  };

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  useEffect(() => {
    const eventId = searchParams.get('eventId');
    const returnTo = searchParams.get('returnTo');
    if (!eventId || events.length === 0) return;

    const eventToOpen = events.find((event) => String(event.id) === String(eventId));
    if (!eventToOpen) return;

    const eventDate = parseEventDate(eventToOpen.date);
    setCurrentMonth(eventDate);
    setSelectedDate(eventDate);
    setSelectedEvent(eventToOpen);
    if (returnTo === 'home-upcoming') {
      setOpenedFromHomeEventId(String(eventToOpen.id));
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('eventId');
    setSearchParams(nextParams, { replace: true });
  }, [events, searchParams, setSearchParams]);

  const closeSelectedEvent = () => {
    if (openedFromHomeEventId && String(selectedEvent?.id) === openedFromHomeEventId) {
      navigate(`${createPageUrl('Home')}#proximos-eventos`);
      return;
    }
    setSelectedEvent(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Agenda" 
            subtitle="Eventos e programações"
            showBack={true}
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4">
        {/* Calendar Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-4"
        >
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold text-gray-900 capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month start */}
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            
            {/* Days of month */}
            {daysInMonth.map(day => {
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const dayEvents = getEventsForDate(day);
              const dayHasEvents = dayEvents.length > 0;
              const isCurrentDay = isToday(day);

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => {
                    setSelectedDate(day);
                    setOpenedFromHomeEventId(null);
                    setSelectedEvent(dayHasEvents ? sortEventsByDateAndTime(dayEvents)[0] : null);
                  }}
                  aria-pressed={Boolean(isSelected)}
                  aria-label={`${format(day, "d 'de' MMMM", { locale: ptBR })}${dayHasEvents ? `, ${dayEvents.length} evento${dayEvents.length > 1 ? 's' : ''}` : ', sem eventos'}`}
                  className={`mbv-agenda-day aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all ${
                    isSelected 
                      ? 'bg-indigo-600 text-white' 
                      : isCurrentDay
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <span className={`text-sm ${isSelected ? 'font-bold' : ''}`}>
                    {format(day, 'd')}
                  </span>
                  {dayHasEvents && (
                    <div className={`w-1.5 h-1.5 rounded-full absolute bottom-1 ${
                      isSelected ? 'bg-white' : 'bg-indigo-500'
                    }`} />
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Events List */}
        <motion.div
          key={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(currentMonth, 'yyyy-MM')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {selectedDate
                ? format(selectedDate, "d 'de' MMMM", { locale: ptBR })
                : `Eventos de ${format(currentMonth, 'MMMM', { locale: ptBR })}`}
            </h3>
            {selectedDate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDate(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {visibleEvents.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center">
              <CalendarIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">
                {selectedDate ? 'Nenhum evento neste dia' : 'Nenhum evento neste mês'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleEvents.map((event, index) => (
                <React.Fragment key={event.id}>
                  {!selectedDate && (index === 0 || visibleEvents[index - 1].date !== event.date) && (
                    <div className="pt-2">
                      <h4 className="text-sm font-semibold text-gray-600 capitalize">
                        {format(parseEventDate(event.date), "EEEE, d 'de' MMMM", { locale: ptBR })}
                      </h4>
                    </div>
                  )}
                  <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                        {event.start_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {event.start_time}
                            {event.end_time && ` - ${event.end_time}`}
                          </span>
                        )}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </div>
                      )}
                    </div>
                    <Badge className={eventTypeColors[event.type] || eventTypeColors['Outro']}>
                      {event.type || 'Evento'}
                    </Badge>
                  </div>
                  </motion.div>
                </React.Fragment>
              ))}
            </div>
          )}
        </motion.div>

        {/* Event Detail Modal */}
        <AnimatePresence>
          {selectedEvent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4"
              onClick={closeSelectedEvent}
            >
              <motion.div
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                exit={{ y: 100 }}
                onClick={(e) => e.stopPropagation()}
                className="mbv-agenda-event-detail bg-white rounded-t-3xl w-full max-w-lg p-6 max-h-[80vh] overflow-auto"
              >
                <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />

                {selectedEventDayEvents.length > 1 && (
                  <div className="mbv-agenda-event-switcher">
                    <button type="button" onClick={() => showAdjacentEvent(-1)} aria-label="Evento anterior"><ChevronLeft className="w-5 h-5" /></button>
                    <span>Evento {selectedEventIndex + 1} de {selectedEventDayEvents.length} neste dia</span>
                    <button type="button" onClick={() => showAdjacentEvent(1)} aria-label="Próximo evento"><ChevronRight className="w-5 h-5" /></button>
                  </div>
                )}
                
                <Badge className={eventTypeColors[selectedEvent.type] || eventTypeColors['Outro']}>
                  {selectedEvent.type || 'Evento'}
                </Badge>
                
                <h2 className="text-xl font-bold text-gray-900 mt-3">{selectedEvent.title}</h2>
                
                <div className="space-y-3 mt-4">
                  <div className="flex items-center gap-3 text-gray-600">
                    <CalendarIcon className="w-5 h-5 text-indigo-500" />
                    <span>{format(parseEventDate(selectedEvent.date), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
                  </div>
                  
                  {selectedEvent.start_time && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <Clock className="w-5 h-5 text-indigo-500" />
                      <span>
                        {selectedEvent.start_time}
                        {selectedEvent.end_time && ` às ${selectedEvent.end_time}`}
                      </span>
                    </div>
                  )}
                  
                  {selectedEvent.location && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <MapPin className="w-5 h-5 text-indigo-500" />
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  
                  {selectedEvent.responsible && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <User className="w-5 h-5 text-indigo-500" />
                      <span>{selectedEvent.responsible}</span>
                    </div>
                  )}
                </div>
                
                {selectedEvent.description && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Descrição</h3>
                    <p className="text-gray-600 whitespace-pre-line">{selectedEvent.description}</p>
                  </div>
                )}
                
                {selectedEvent.observations && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-xl">
                    <h3 className="font-semibold text-amber-800 mb-1">Observações</h3>
                    <p className="text-amber-700 text-sm">{selectedEvent.observations}</p>
                  </div>
                )}
                
                <Button 
                  className="w-full mt-6"
                  onClick={closeSelectedEvent}
                >
                  Fechar
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

